<script module lang="ts">
  import type { ColorVariant } from '@agapi/ui/types';

  export interface Props {
    data: LayoutServerResponse;
    colorVariant?: ColorVariant;
    children?: Snippet;
  }

  let cachedData: LayoutServerResponse | null = null;
</script>

<script lang="ts">
  import { page, updated } from '$app/state';
  import { beforeNavigate, afterNavigate } from '$app/navigation';
  import { browser } from '$app/environment';

  import { PUBLIC_ENVIRONMENT, PUBLIC_LIPSCORE_API_KEY } from '$env/static/public';

  import { setContext, untrack, type Snippet } from 'svelte';
  // import NProgress from 'nprogress';

  import { css } from '@agapi/ui/panda/css';

  import type { LayoutServerResponse } from './server';
  import { EVENTS } from '../../services';
  import { Navbar, Footer, LocationDialog } from '../../components';

  import {
    clientEventEmitter,
    initializeSession,
    getLocaleFromUrl,
    captureEvent,
    identifyUserFromCookie
  } from '../../utils';

  import { createCartStore, createCollectionStore } from '../../stores';
  import { createInstanceStore } from '../../stores/instance';
  import { setCollectionContext } from '../../contexts';
  import { getClientCountry } from '../../clients/country.client';
  import { getSession } from '../../services/shop.client';

  import { setLocale } from '$lib/parachute/runtime';

  setLocale(getLocaleFromUrl(page.url).language);

  // import { i18n } from '$lib/i18n';
  // import { isDesktop, isTablet } from '../../utils/device.client';

  // See https://github.com/rstacruz/nprogress#configuration
  // NProgress.configure({ minimum: 0.16, showSpinner: false, parent: '#navbar-root' });

  console.log('[layout.svelte]: Adding global listener');

  EVENTS.addGlobalListener('storefront-events', async (event, values) => {
    await clientEventEmitter(event, values, cart);
    // TODO: add a serverEventEmitter which calls a svelte api that handles the event on the server side
  });

  // Retrieve 'height' from navbar as we cannot use the var(--navbarBottom) as it causes paddin-top issues when scrolled and refreshed
  let { children, data, colorVariant }: Props = $props();

  const instance = setContext('instance-store', createInstanceStore());
  const cart = setContext('cart-store', createCartStore());
  setCollectionContext(createCollectionStore());

  let navData = $state(data);
  let clientCountry: string | undefined = $state(undefined);

  let head: { robots?: string } = $state({});

  $effect(() => {
    // Runs on both SSR and SPA
    if (PUBLIC_ENVIRONMENT === 'prod') {
      if (page.data.pageData?.meta?.robots) {
        head.robots = page.data.pageData?.meta?.robots;
      } else {
        head.robots = 'index, follow';
      }
    } else {
      head.robots = 'noindex, nofollow';
    }
  });

  $effect(() => {
    untrack(async () => {
      if (browser) {
        console.log('[layout.svelte]: loading session');
        const { language: languageCode, country: countryCode } = getLocaleFromUrl(page.url);
        const session = await getSession(fetch, { languageCode, countryCode });
        initializeSession(session.session, cart, instance, page.url);

        // Reinitialize session if other tabs/windows have a new session
        instance.on('init', (event) => {
          const session = event.session;
          initializeSession(session, cart, instance, page.url);
          // Re-identify user with PostHog when session changes
          identifyUserFromCookie();
        });

        clientCountry = (await getClientCountry(globalThis.fetch, page.url.origin))?.country;
      }

      if (!cachedData) {
        cachedData = data;
      } else if (cachedData) {
        navData = cachedData;
      }
    });
  });

  let timeout: number | null = $state(null);

  beforeNavigate(({ willUnload, to }) => {
    /*
    // Mobile does already have a strong loading indicator natively, only use for desktop
    if (isDesktop() || isTablet()) {
      // Only show if there are actualy large latencies (slow responses)
      timeout = setTimeout(() => {
        NProgress.configure({ minimum: 0.3, speed: 200 });
        NProgress.set(0.1);
        NProgress.start();
        console.log('NProgress.start');
      }, 150) as unknown as number; // Tweak this timout if needed
    }
    */

    // Track pageleaves with PostHog
    captureEvent('$pageleave');

    // Navigation history
    if (updated.current && !willUnload && to?.url) {
      location.href = to.url.href;
    }
  });

  afterNavigate(() => {
    // NOTE: we can potentially calculate the end speed depending on elapsed time?
    // Speed up if completed (200ms gives a slow feeling, 50ms gives a stressed feeling, 100ms is a good balance)

    /*
    if (isDesktop() || isTablet()) {
      if (timeout) {
        clearTimeout(timeout);
      }

      NProgress.configure({ speed: 100 });
      NProgress.done();
    }
    */
    // Track pageviews with PostHog
    captureEvent('$pageview');
  });

  $effect(() => {
    if (typeof gtag !== 'undefined') {
      gtag('config', 'G-X8HBH0N091', {
        page_title: document.title,
        page_path: page.url.pathname
      });

      gtag('consent', 'default', {
        ad_storage: 'denied',
        analytics_storage: 'denied'
      });
    }
  });
</script>

<svelte:head>
  <link rel="dns-prefetch" href="//a.storyblok.com/" />
  <link rel="dns-prefetch" href="//glimja.b-cdn.net/" />

  <meta name="robots" content={head.robots} />

  <!-- Lipscore -->
  {@html `<script defer type="text/javascript">
    // Get user's locale or default to Swedish (handles by Paraglide)
    const lipscoreLocale = document.documentElement.lang === 'nb' ? 'no' : (document.documentElement.lang || 'sv');

    // Initialize Lipscore review system
    window.lipscoreInit = () => lipscore.init({ apiKey: '${PUBLIC_LIPSCORE_API_KEY}' });

    // Load Lipscore script
    (() => {
      const script = document.createElement('script');
      script.defer = true;
      script.src = '//static.lipscore.com/assets/' + lipscoreLocale + '/lipscore-v1.js';
      document.head.appendChild(script);
    })();
  </script>`}
</svelte:head>

<Navbar colorVariant={colorVariant} navData={navData.navData} />

<main
  id="main-content"
  class={css({ display: 'flex', flexDirection: 'column', overflowAnchor: 'none' })}
  style={`padding-top: calc(var(--navbarBottom) + ${ !browser && navData.navData?.banners?.length ? '34px' : '0rem'})`}
>
  {@render children?.()}
</main>

<Footer footerData={navData.footerData} />

<LocationDialog clientCountry={clientCountry} />

<style global>
  :root {
    --nprogress-primary-color: #c6e6e7;
    --nprogress-secondary-color: #027962;
    --nprogress-height: 4px;
  }

  #nprogress {
    pointer-events: none;
  }

  #nprogress .bar {
    background: var(--nprogress-primary-color);

    position: relative;
    z-index: 1031;
    bottom: calc(var(--underHeaderHeight) - 4px);
    left: 0;

    width: 100%;
    height: var(--nprogress-height);
  }

  /* Remove these to get rid of the spinner */
  #nprogress .spinner {
    display: block;
    position: fixed;
    z-index: 1031;
    top: 10px;
    right: 10px;
  }

  #nprogress .spinner-icon {
    width: 18px;
    height: 18px;
    box-sizing: border-box;

    border: solid 2px transparent;
    border-top-color: var(--nprogress-secondary-color);
    border-left-color: var(--nprogress-secondary-color);
    border-radius: 50%;

    -webkit-animation: nprogress-spinner 400ms linear infinite;
    animation: nprogress-spinner 400ms linear infinite;
  }

  .nprogress-custom-parent {
    overflow: hidden;
    position: relative;
  }

  .nprogress-custom-parent #nprogress .bar {
    position: absolute;
  }

  @-webkit-keyframes nprogress-spinner {
    0% {
      -webkit-transform: rotate(0deg);
    }
    100% {
      -webkit-transform: rotate(360deg);
    }
  }
  @keyframes nprogress-spinner {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
</style>
